import { useCallback, useMemo } from 'react';

import {
  EventDate,
  EventDatesResponse,
  useListEventDates,
  useShowCurrentCampaign,
} from '#api/index.js';
import { MultiSelect } from 'ui';
import { UseFormReturnType } from 'ui/Form';

import { AddEditTicketFormType } from '../AddTicketForm.type';

type IncludedEventDatesProps = {
  form: UseFormReturnType<AddEditTicketFormType>;
};

const IncludedEventDates = ({ form }: IncludedEventDatesProps) => {
  const { data: event } = useShowCurrentCampaign();
  const { data: eventDates } = useListEventDates({
    eventId: event?.uuid,
    causeId: event?.cause_id,
    select: useCallback((data: EventDatesResponse<EventDate[]>) => data.event_dates, []),
  });

  const eventDatesData = useMemo(() => {
    const dates = eventDates?.map((eventDate) => ({
      value: eventDate.uuid,
      label: eventDate.title,
      disabled: form.values.included_event_dates?.includes('*'),
    }));

    const data = [
      {
        value: '*',
        label: 'All Event Dates & Times',
        disabled: eventDates?.length === 0,
      },
      {
        group: 'Event Dates',
        items: dates ?? [],
      },
    ];

    return data;
  }, [eventDates, form.values.included_event_dates]);

  const onValuesChange = useCallback((values: string[]) => {
    // If the user selects all event dates we want to update the multi-select to only show the all event dates option.
    if (values?.includes('*') && values.length > 1) {
      values = ['*'];
    }
    form.setFieldValue('included_event_dates', values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MultiSelect
      label="Included Event Dates & Times"
      required
      data={eventDatesData}
      {...form.getInputProps('included_event_dates')}
      onChange={onValuesChange}
    />
  );
};

export { IncludedEventDates };
